import { useEffect, useRef, useState, type ReactNode } from "react";
import type { AdminName } from "../../../../shared/protocol";
import type { KeptAdmin } from "../../lib/session";
import type { Tournament } from "../../lib/useTournament";
import { PinEntry, lockedWords } from "./Pin";

/** Three taps this close together open the hidden way in. */
const TRIPLE_MS = 1000;
/** Without a top bar on the page (the join steps), a tap this near the top of the glass is on it. */
const TOP_PX = 80;
/** Taps on these are their own; they never count. */
const KEYS = "button, a, input, select, textarea, label, [role=button], .pin-door";

/**
 * Three quick taps on the top bar of the player's phone (decision 40). Taps on keys do not count,
 * so the picture and the profile link still open on the first tap.
 */
export function useTripleTap(onTriple: () => void): void {
  const latest = useRef(onTriple);
  latest.current = onTriple;
  useEffect(() => {
    let taps: number[] = [];
    const down = (e: PointerEvent) => {
      const at = e.target instanceof Element ? e.target : null;
      if (!at || at.closest(KEYS)) return;
      const bar = document.querySelector(".player-who");
      const onBar = bar ? bar.contains(at) : e.clientY < TOP_PX;
      if (!onBar) {
        taps = [];
        return;
      }
      const now = Date.now();
      taps = [...taps.filter((t) => now - t < TRIPLE_MS), now];
      if (taps.length >= 3) {
        taps = [];
        latest.current();
      }
    };
    window.addEventListener("pointerdown", down, true);
    return () => window.removeEventListener("pointerdown", down, true);
  }, []);
}

/** The admin screen for an admin link's token, in the same tournament (and the same pretend day). */
export function adminAddress(token: string): string {
  const here = new URLSearchParams(location.search);
  const q = new URLSearchParams();
  for (const k of ["t", "mock", "speed"]) {
    const v = here.get(k);
    if (v !== null) q.set(k, v);
  }
  const query = q.toString();
  return `/a/${encodeURIComponent(token)}${query ? `?${query}` : ""}`;
}

type Step =
  | { at: "link"; kept: KeptAdmin }
  | { at: "pick"; admins: AdminName[]; pausedUntil: number | null }
  | { at: "pin"; admin: AdminName };

/**
 * The hidden way into the admin side from a player's phone (decision 40). A phone that has opened
 * an admin link asks only for that admin's PIN. Any other phone shows the named admins who have
 * set a PIN (never the owner), then asks for the PIN of the one picked. The right PIN opens the
 * admin screen, and this phone keeps that admin's session. With nobody to pick, nothing shows.
 */
export function useDoor(t: Tournament): { open: () => void; sheet: ReactNode } {
  const [step, setStep] = useState<Step | null>(null);
  const busy = useRef(false);

  async function open() {
    if (step || busy.current) return;
    const kept = t.keeper.admin();
    if (kept) {
      setStep({ at: "link", kept });
      return;
    }
    await pickList();
  }

  /** The named admins to pick from. With nobody to pick, nothing changes: no sheet, or the one there says why. */
  async function pickList() {
    busy.current = true;
    const a = await t.adminNames();
    busy.current = false;
    if (!a.ok || !a.admins?.length) return;
    setStep({ at: "pick", admins: a.admins, pausedUntil: a.pausedUntil ?? null });
  }

  const go = (token: string) => location.assign(adminAddress(token));
  const close = () => setStep(null);

  let body: ReactNode = null;
  if (step?.at === "link") {
    const kept = step.kept;
    body = (
      <>
        <h2>Admin PIN</h2>
        <p>{kept.owner ? "The owner's admin PIN." : kept.name ? `${kept.name}'s admin PIN.` : "Your admin PIN."}</p>
        <PinEntry
          mode="enter"
          yes="Open the admin screen"
          send={async (pin) => {
            const a = await t.adminOpen(pin, { link: kept.token });
            if (a.ok && a.token) go(a.token);
            // no admin PIN yet: the admin screen itself asks for a new one
            else if (!a.ok && a.set === false) go(kept.token);
            // that admin was removed or renewed: forget the link and offer the names instead
            else if (!a.ok && a.stopped) {
              t.keeper.saveAdmin(null);
              void pickList();
            }
            return a;
          }}
        />
      </>
    );
  } else if (step?.at === "pick") {
    const paused = step.pausedUntil !== null && step.pausedUntil > Date.now();
    body = (
      <>
        <h2>Admin</h2>
        {paused && step.pausedUntil !== null ? (
          <p className="pin-problem" role="alert">{lockedWords(step.pausedUntil)}</p>
        ) : (
          <>
            <p>Which admin are you?</p>
            <div className="pin-door-names">
              {step.admins.map((a) => (
                <button key={a.id} type="button" className="btn" onClick={() => setStep({ at: "pin", admin: a })}>
                  {a.name}
                </button>
              ))}
            </div>
          </>
        )}
      </>
    );
  } else if (step?.at === "pin") {
    const admin = step.admin;
    body = (
      <>
        <h2>Admin PIN</h2>
        <p>{admin.name}'s admin PIN.</p>
        <PinEntry
          mode="enter"
          yes="Open the admin screen"
          send={async (pin) => {
            const a = await t.adminOpen(pin, { admin: admin.id });
            if (a.ok && a.token) go(a.token);
            return a;
          }}
        />
      </>
    );
  }

  const sheet = body ? (
    <div className="pin-door" role="dialog" aria-modal="true" aria-label="Admin">
      {body}
      <button type="button" className="btn quiet pin-go" onClick={close}>
        Back to my phone
      </button>
    </div>
  ) : null;
  return { open: () => void open(), sheet };
}
